import { rgba } from "@similarweb/styles";

import type { ICategory, ICustomCategory } from "app/common/services/categoryService.types";
import { toTimeGranularityName, type TimeGranularitySymbol } from "app/common/utils/timeGranularity";
import { translate } from "app/filters/i18nFilter";
import {
    abbrNumberFilter,
    percentagePointFilter,
    percentageSignFilter,
    yAxisChangeFilter,
} from "app/filters/ngFilters";
import { getIndustryQueryParams, getQueryParams } from "app/pages/industry-analysis/common/getQueryParams";
import { getSelectedCategory } from "app/pages/industry-analysis/common/getSelectedCategory";
import type { Records } from "app/pages/industry-analysis/players/table/types";
import type { ICustomSegment } from "app/services/segments/segmentsApiService";
import { calcChange } from "app/UtilitiesAndConstants/UtilityFunctions/change";
import { EChartDisplayType } from "pro-features/components/Chart/src/configs/column-chart/columnChartConfig";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { IEnrichedDataMetrics } from "../types";
import { EApiKeys } from "../types";

interface IMetaData {
    apiKey: EApiKeys;
    formatter: (value: number) => string;
    averageValueMetric: keyof IEnrichedDataMetrics;
}

type ChartDisplayTypeMD = Record<EChartDisplayType, IMetaData>;

export const CHART_DISPLAY_TYPE_MD: ChartDisplayTypeMD = {
    [EChartDisplayType.ABSOLUTE]: {
        apiKey: EApiKeys.ABSOLUTE,
        // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
        formatter: (value) => abbrNumberFilter()(value, 0),
        averageValueMetric: "monthlyVisits",
    },
    [EChartDisplayType.PERCENTAGE]: {
        apiKey: EApiKeys.SHARE,
        formatter: (value) => percentageSignFilter()(value, 2),
        averageValueMetric: "trafficShare",
    },
};

export const getApiKey = (displayType: EChartDisplayType): EApiKeys => {
    return CHART_DISPLAY_TYPE_MD[displayType].apiKey;
};

export const dashboardDataDisplayType = {
    [EChartDisplayType.ABSOLUTE]: "number",
    [EChartDisplayType.PERCENTAGE]: "percent",
} as const;

export const chartDisplayTypeFromDataMode = {
    number: EChartDisplayType.ABSOLUTE,
    percent: EChartDisplayType.PERCENTAGE,
} as const satisfies Record<string, EChartDisplayType>;

export const dataDisplayTypeForUrl = {
    number: "ABSOLUTE",
    percent: "PERCENTAGE",
};

export const getFadedColumnColor = (originalColor: string) => rgba(originalColor, 0.3);

export const getPeriodOverPeriodValueChange = (
    currentValue: number,
    previousValue: number,
    chartDisplayType: EChartDisplayType,
): string => {
    return chartDisplayType === EChartDisplayType.PERCENTAGE
        ? percentagePointFilter(currentValue, previousValue)
        : yAxisChangeFilter()(calcChange(currentValue, previousValue), 2);
};

export const getAdjacentPeriodChangeById = (...recordSets: Array<Records | undefined>): Map<string, number> =>
    new Map(
        recordSets
            .flatMap((records) => records ?? [])
            .filter(({ AdjacentPeriodChange }) => Number.isFinite(AdjacentPeriodChange))
            .map(({ Id, AdjacentPeriodChange }) => [Id, AdjacentPeriodChange]),
    );

export function getMarketSharesQueryParams(
    timeGranularity: TimeGranularitySymbol = "M",
    isMonthToDate?: boolean,
): Record<string, unknown> {
    const baseQueryParams = getIndustryQueryParams();
    const selectedCategory = getSelectedCategory();
    const params = proNavigator.getParams();
    const queryParams = {
        ...baseQueryParams,
        timeGranularity: toTimeGranularityName(timeGranularity),
        category: selectedCategory.categoryId,
        latest: isMonthToDate ? "l" : undefined,
        includeRegionalDomains: params.includeRegionalDomains === "true",
    };

    return queryParams;
}

/**
 * Same shape as getMarketSharesQueryParams, but scoped to a specific category instead of
 * the globally "selected" one — needed for comparison mode, where each compared category
 * must carry its own categoryHash rather than borrowing the primary category's.
 */
export function getMarketSharesQueryParamsForCategory(
    category: ICategory | ICustomCategory,
    timeGranularity: TimeGranularitySymbol = "M",
    isMonthToDate?: boolean,
): Record<string, unknown> {
    const baseQueryParams = getQueryParams();
    const params = proNavigator.getParams();
    const queryParams = {
        ...baseQueryParams,
        keys: category.forApi,
        categoryHash: "categoryHash" in category ? category.categoryHash : "",
        timeGranularity: toTimeGranularityName(timeGranularity),
        category: category.categoryId,
        latest: isMonthToDate ? "l" : undefined,
        includeRegionalDomains: params.includeRegionalDomains === "true",
    };

    return queryParams;
}

export const getDomainSubtitle = (segment?: ICustomSegment, regionalDomainsCount?: number) => {
    if (segment) {
        return segment.segmentName;
    }

    if (regionalDomainsCount > 1) {
        const otherCount = regionalDomainsCount - 1;
        return translate(
            otherCount === 1
                ? "market.players.regionals.other.domains.singular"
                : "market.players.regionals.other.domains",
            {
                countOfOtherDomains: otherCount,
            },
        );
    }

    return null;
};

export const shouldDisplayOthers = (isPercentage: boolean, othersChecked: boolean): boolean => {
    // Simple check: show Others if it's checked and we're in percentage mode
    return isPercentage && othersChecked;
};

export const shouldDisableOthers = (visibleDomainCount: number, totalAvailableDomainCount: number): boolean => {
    // Disable Others checkbox when all available domains are selected
    return visibleDomainCount >= totalAvailableDomainCount;
};
